/**
 * web/src/lib/uiState.ts — session-scoped UI memory (UX item 5, findings H5/H6).
 *
 * The report: moving between tabs threw away everything you had half-done. A
 * draft in the Thread composer, the filter chip you had narrowed the stream to,
 * and where you had scrolled were all component state, and Pear unmounts a
 * panel the moment you leave its tab (App.tsx holds lifeTab / usTab / filters
 * at the top for exactly this reason — this is the same fix, generalised to the
 * state that was still local).
 *
 * Two rules make it safe:
 *
 *   1. sessionStorage, not localStorage. A draft is scoped to THIS tab's
 *      session — it survives a reload, a nav round trip and a tab switch, and
 *      it dies with the tab. It is not synced to another device and not shared
 *      between two browser windows.
 *   2. Nothing here is a source of truth. Every read is a fallback for a
 *      missing/rejected state, so a cleared or corrupt store degrades to the
 *      caller's initial value instead of breaking the screen.
 *
 * Scroll memory carries one extra bit: whether the reader was parked at the
 * bottom. A Thread restored to its bottom lands on new messages (the old
 * behaviour); one restored mid-history stays where it was, which is the whole
 * point of remembering it.
 */

const STORE_KEY = 'pear_ui_v1';

type ScrollRecord = { top: number; atBottom: boolean };

type Bag = Record<string, unknown>;

function readStore(): Bag {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Bag) : {};
  } catch {
    return {};
  }
}

let bag: Bag = readStore();

function persist(): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(bag));
  } catch {
    /* Private mode / quota: memory is an optimisation, never a requirement. */
  }
}

/** Remember a small serialisable value under `name`. */
export function remember<T>(name: string, value: T): void {
  bag[name] = value;
  persist();
}

/** The value last remembered under `name`, or null. */
export function recall<T>(name: string): T | null {
  const v = bag[name];
  return v === undefined || v === null ? null : (v as T);
}

/** Forget one entry (used when a draft is sent or a memory is applied). */
export function forget(name: string): void {
  delete bag[name];
  persist();
}

/** Clear every entry — sign-out must not leave a draft behind. */
export function forgetAll(): void {
  bag = {};
  persist();
}

export const SCROLL_PREFIX = 'scroll:';

/** Remember the scroll position of one named container. */
export function rememberScroll(name: string, rec: ScrollRecord): void {
  remember(SCROLL_PREFIX + name, rec);
}

export function recallScroll(name: string): ScrollRecord | null {
  const rec = recall<ScrollRecord>(SCROLL_PREFIX + name);
  return rec && typeof rec.top === 'number' ? rec : null;
}
